/**
 * Body of `ConnectionDiagnosticsDialog` — composes the headline, sign-in row,
 * facts table, soft hints, underlying error block, and footer (Copy details +
 * Send diagnostic report).
 *
 * Split out of the parent dialog to keep both files under the 200-line guideline.
 */

import { Button } from '../../../components/ui/button';
import type { DiagnosticsReport } from '../../../types/connectionDiagnostics';
import type { CrashReportMode } from '../../crash-dialog/types/crashTypes';
import { DiagnosticsFactsTable } from './DiagnosticsFactsTable';
import { DiagnosticsHeadline } from './DiagnosticsHeadline';
import { DiagnosticsSignInRow } from './DiagnosticsSignInRow';
import { DiagnosticsSoftHints } from './DiagnosticsSoftHints';
import { DiagnosticsUnderlyingError } from './DiagnosticsUnderlyingError';

export type DiagnosticsSendState = 'idle' | 'sending' | 'sent' | 'failed';

/**
 * Reconnect button state — only ever visible when the listing failed with
 * `listingErrorReason: 'no_pool'`. Slice 11 wires this to
 * `electronAPI.connectToDatabase(connection)` for the active connection.
 */
export type DiagnosticsReconnectState =
  | 'idle'
  | 'reconnecting'
  | 'failed';

type DialogBodyProps = {
  report: DiagnosticsReport;
  sendState: DiagnosticsSendState;
  showSendButton: boolean;
  crashReportMode: CrashReportMode;
  onCopy: () => void;
  onSend: () => void;
  /** Callback invoked when the user clicks Reconnect. Only active for `'no_pool'`. */
  onReconnect?: () => void;
  reconnectState?: DiagnosticsReconnectState;
};

export function DiagnosticsDialogBody({
  report,
  sendState,
  showSendButton,
  crashReportMode,
  onCopy,
  onSend,
  onReconnect,
  reconnectState = 'idle',
}: DialogBodyProps) {
  const isNoPool = report.databases.listingErrorReason === 'no_pool';
  const showReconnectButton = isNoPool && typeof onReconnect === 'function';
  if (report.connectionUnavailable) {
    return (
      <div className="space-y-4" data-testid="diag-connection-unavailable">
        <p className="text-sm">
          This connection isn't currently active in the app, so we can't probe
          the server. Reconnect (right-click the connection in Object Explorer
          → Disconnect, then expand it again) and reopen this dialog to see
          the full diagnostics.
        </p>
        <p className="text-xs text-muted-foreground">
          This usually happens after a session restore that didn't reopen the
          pool, or after the connection dropped on the server side.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <DiagnosticsHeadline report={report} />
      <DiagnosticsSignInRow report={report} />
      <DiagnosticsFactsTable report={report} />
      <DiagnosticsSoftHints report={report} />
      <DiagnosticsUnderlyingError report={report} />
      {report.notImplementedFor && (
        <p className="text-sm text-muted-foreground">
          Diagnostics aren't implemented for the {report.notImplementedFor} engine yet.
        </p>
      )}
      <div className="flex flex-col gap-3 border-t border-border pt-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground max-w-md">
          Sends only error codes, counts, capability flags, and your app version. No
          query text, account name, server name, or database names.
        </p>
        <div className="flex items-center gap-2 self-end sm:self-auto">
          <Button
            type="button"
            variant="outline"
            data-testid="diag-copy-btn"
            onClick={onCopy}
          >
            Copy details
          </Button>
          {showSendButton && (
            <Button
              type="button"
              data-testid="diag-send-btn"
              disabled={sendState === 'sent' || sendState === 'sending'}
              onClick={onSend}
            >
              {sendState === 'sent'
                ? 'Sent ✓'
                : sendState === 'sending'
                  ? 'Sending…'
                  : 'Send diagnostic report'}
            </Button>
          )}
          {showReconnectButton && (
            <Button
              type="button"
              data-testid="diag-reconnect-btn"
              disabled={reconnectState === 'reconnecting'}
              onClick={onReconnect}
            >
              {reconnectState === 'reconnecting' ? 'Reconnecting…' : 'Reconnect'}
            </Button>
          )}
          {!showSendButton && crashReportMode === 'never' && (
            <p className="text-xs text-muted-foreground">
              Sending diagnostics is disabled in Privacy settings.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function DiagnosticsLoadingSkeleton() {
  return (
    <div data-testid="diag-loading-skeleton" className="space-y-3 animate-pulse">
      <div className="h-4 w-3/4 bg-muted rounded" />
      <div className="h-4 w-1/2 bg-muted rounded" />
      <div className="h-24 w-full bg-muted/60 rounded" />
      <div className="h-3 w-2/3 bg-muted rounded" />
    </div>
  );
}
